import type { Vec2 } from '../../../../geo/types';
import { orientedFace, planPoint } from '../../solids';
import { rectWall, roofUv, slopedWall } from '../masonry';
import type { ChapelPlan, SidePlan } from '../churchModel';
import { buildChapel } from '../churchSideParts';
import { fittedButtress } from '../oudeKerk/oudeKerkButtress';
import { bayWindows, LIGHTS, type NieuweKerkBuild } from './nieuweKerkParts';

/**
 * The aisles of the nave under lean-to roofs against the clerestory, the outer aisles over the two
 * eastern bays with a gable to each bay, and the chapels beside the choir: the Eggert chapel to the
 * south, where Willem Eggert (d. 1417) lies buried, the Holy Cross and Lady chapels to the north (De
 * Gids 1942, pp. 28-29, 36). All but the Eggert chapel, of the south side before 1452 and so of tuff,
 * are brick banded with stone (p. 31). Buttresses stand at the bays, crowned with pinnacles on the
 * chapels as the painting draws the east end (nieuwekerk-scan).
 */
const BUTTRESS = { width: 0.85, depth: 1.4, drop: 0.8 } as const;
const PINNACLED = { width: 0.8, depth: 1.6, rise: 0.6, pinnacle: 2.4 } as const;

function buildLeanTo(
  s: NieuweKerkBuild,
  a: SidePlan,
  covered: (u0: number, u1: number) => boolean
): void {
  const north = a.side > 0;
  const bays = s.plan.naveBays;
  const [p, q] = north ? [a.u1, a.u0] : [a.u0, a.u1];
  const windows = bayWindows(bays, p, north ? -1 : 1, LIGHTS.aisle, covered);
  rectWall(
    s.builder,
    s.brick,
    s.frame,
    [
      [p, a.outer],
      [q, a.outer],
    ],
    [0, a.eaveY],
    windows
  );
  rectWall(
    s.builder,
    s.brick,
    s.frame,
    [
      [q, a.inner],
      [p, a.inner],
    ],
    [0, a.topY]
  );
  slopedWall(
    s.builder,
    s.brick,
    s.frame,
    [
      [q, a.outer],
      [q, a.inner],
    ],
    a.eaveY,
    a.topY
  );
  slopedWall(
    s.builder,
    s.brick,
    s.frame,
    [
      [p, a.inner],
      [p, a.outer],
    ],
    a.topY,
    a.eaveY
  );
  const points = [
    planPoint(a.u0, a.eaveY, a.outer),
    planPoint(a.u1, a.eaveY, a.outer),
    planPoint(a.u1, a.topY, a.inner),
    planPoint(a.u0, a.topY, a.inner),
  ];
  orientedFace(
    s.builder,
    s.stone.roof,
    s.frame,
    points,
    [0, 1, -a.side],
    roofUv(points[0]!, points[1]!, points[2]!)
  );
}

function buildAisles(s: NieuweKerkBuild): void {
  const eastmost = Math.min(...s.plan.outerAisles.map((c) => c.u0));
  const covered = (u0: number): boolean => u0 >= eastmost - 1;
  for (const a of s.plan.aisles) {
    buildLeanTo(s, a, covered);
    const spec = {
      width: BUTTRESS.width,
      depth: BUTTRESS.depth,
      height: a.eaveY - BUTTRESS.drop,
      pinnacle: 0,
    };
    const lines = [
      a.u0 + BUTTRESS.width / 2,
      ...s.plan.naveBays.slice(1, -1).filter((u) => u < eastmost - 1e-6),
    ];
    for (const u of lines) fittedButtress(s, [u, a.outer], [0, a.side], spec);
  }
}

/** Chapels in a row with a pinnacled buttress at every boundary; those at the row's ends stand inside its corners. */
function buildChapelRow(
  s: NieuweKerkBuild,
  row: readonly ChapelPlan[],
  styles: NieuweKerkBuild['stone']
): void {
  for (const c of row) buildChapel(s.builder, styles, s.frame, c);
  const bounds = [...new Set(row.flatMap((c) => [c.u0, c.u1]))].sort((x, y) => x - y);
  const half = PINNACLED.width / 2;
  bounds.forEach((u, k) => {
    const c = row.find((r) => r.u0 === u || r.u1 === u)!;
    const at: Vec2 = [k === 0 ? u + half : k === bounds.length - 1 ? u - half : u, c.outer];
    const spec = {
      width: PINNACLED.width,
      depth: PINNACLED.depth,
      height: c.eaveY + PINNACLED.rise,
      pinnacle: PINNACLED.pinnacle,
    };
    fittedButtress(s, at, [0, c.side], spec);
  });
}

export function buildAislesAndChapels(s: NieuweKerkBuild): void {
  buildAisles(s);
  for (const side of [1, -1] as const) {
    buildChapelRow(
      s,
      s.plan.outerAisles.filter((c) => c.side === side),
      s.brick
    );
    buildChapelRow(
      s,
      s.plan.choirChapels.filter((c) => c.side === side),
      side > 0 ? s.brick : s.stone
    );
  }
}
